/**
 * Plain-text radiological report, structurally matching the real
 * `{id}_automatic_radiological_report.txt` (`dwi_radiology_report.py`/
 * `pwi_radiology_report.py`) — same sections and real computed numbers, not
 * a byte-identical NLG port (the real narrative-paragraph wording logic
 * (territory-name selection, phrase templates) was not reverse-engineered
 * line-for-line; this produces an equivalent, real-data-driven summary in
 * the same shape instead). ASPECTS total score IS the real formula
 * (`dwi_radiology_report.py:870`: `10 - injured`, injured = count of
 * AFFECTED-called ASPECTS regions).
 */
import type { LesionVolumes } from "./volumes";
import type { HydroCall, InterpretationResult, RegionCall } from "./interpretation";

function table(title: string, calls: RegionCall[]): string {
  const lines = [
    "-".repeat(102),
    title.padStart((102 + title.length) / 2).padEnd(102),
    "-".repeat(102),
    "ROI".padEnd(32) + "predict".padStart(10) + "predict probability".padStart(22) + "QFV(portion of region injured)".padStart(34),
  ];
  for (const c of calls) {
    const predictStr = c.predict === "AFFECTED" ? "1" : c.predict === "NORMAL" ? "0" : "N.A.";
    const probStr = c.probability == null ? "N.A." : c.probability.toFixed(3);
    lines.push(c.region.padEnd(32) + predictStr.padStart(10) + probStr.padStart(22) + "see QFV csv".padStart(34));
  }
  lines.push("-".repeat(102));
  return lines.join("\n");
}

export function buildTextReport(
  subjectId: string,
  modality: "DWI" | "PWI",
  volumes: LesionVolumes,
  interpretation: InterpretationResult,
  /** Native hydro RF call (`interpretation.ts` `computeHydrocephalus`); omitted when no MNI ADC was available. */
  hydrocephalus?: HydroCall,
): string {
  const laterality =
    volumes.leftMl > 0 && volumes.rightMl > 0
      ? "bilateral"
      : volumes.leftMl > volumes.rightMl
        ? "unilateral, within the left brain hemisphere,"
        : volumes.rightMl > 0
          ? "unilateral, within the right brain hemisphere,"
          : "absent";

  const lesionWord = modality === "DWI" ? "restricted diffusion" : "hypoperfusion";
  const affectedVascular = interpretation.vascular.filter((c) => c.predict === "AFFECTED").map((c) => c.region);
  const affectedLobe = interpretation.lobe.filter((c) => c.predict === "AFFECTED").map((c) => c.region);
  const injuredAspects = interpretation.aspects.filter((c) => c.predict === "AFFECTED").length;
  const aspectsTotal = interpretation.aspects.length > 0 ? 10 - injuredAspects : null;

  const header = "-".repeat(37) + "Automatic Radiological Report" + "-".repeat(37);
  const summary = [
    `Area of ${lesionWord} is ${laterality} with ${volumes.totalMl.toFixed(2)} ml,`,
    affectedVascular.length
      ? `in the territory of ${affectedVascular.join(", ")}.`
      : "with no significant vascular territory overlap detected.",
    affectedLobe.length
      ? `The area involves the following brain regions: ${affectedLobe.join(", ")}.`
      : "No significant anatomical structure overlap detected.",
    // `dwi_radiology_report.py:862` (`hydro_flag` is always False from run_ads_dwi.py).
    hydrocephalus
      ? hydrocephalus.predict === "AFFECTED" ? "There is hydrocephalus." : "There is no hydrocephalus."
      : "Hydrocephalus: not evaluated (no MNI-space ADC available to this report).",
    aspectsTotal != null ? `The predicted ASPECTS is ${aspectsTotal}.` : "",
  ]
    .filter(Boolean)
    .join("\n");

  const sections = [
    header,
    "",
    summary,
    "",
    table("Arterial territories affected", interpretation.vascular),
    "",
    table("Anatomical structures affected", interpretation.lobe),
    "",
    ...(hydrocephalus
      ? [table("Hydrocephalus", [{ region: "hydrocephalus", predict: hydrocephalus.predict, probability: hydrocephalus.probability, volumeMl: 0 }]).replace("see QFV csv".padStart(34), "N.A.".padStart(34)), ""]
      : []),
    table("ASPECTS regions affected", interpretation.aspects),
    "",
    `Subject: ${subjectId} | Modality: ${modality} | Left ${volumes.leftMl.toFixed(2)} mL / Right ${volumes.rightMl.toFixed(2)} mL`,
  ];
  return sections.join("\n") + "\n";
}
